'use client'

import {
	STORAGE_ENCRYPTED_SEED,
	STORAGE_ENCRYPTED_SEED_TYPE,
	STORAGE_ENCRYPTED_SEED_SALT,
	STORAGE_PUBLIC_KEY,
	STORAGE_LAST_ACTIVE_AT,
	STORAGE_TAPROOT_ADDRESS,
	STORAGE_WALLET_TYPE
} from '@/utils/constants'

import Event from '@/utils/event'
import { bindPasswordLock } from '@/utils/password-keyboard'
import { validateMnemonic } from '@/utils/mnemonic'
import SafeLocalStorage from './safe-local-storage'
import OrdinalsWallet from '@/utils/wallet'

let PASSWORD = ''

const DEFAULT_DURATION = 1000 * 60 * 15 // 15 Minutes
const OVERWRITE = true
let DID_INIT_PASSWORD_LOOP = false

// Legacy at-rest seed is AES-256-CTR (WASM) with this fixed IV and no auth tag.
// New writes are AES-256-GCM (`gcm1:` + iv || ciphertext). Unprefixed blobs stay CTR.
const LEGACY_SEED_IV = Buffer.from('00000000000000000000000000000001', 'hex')
const SEED_GCM_PREFIX = 'gcm1:'
const SEED_GCM_IV_BYTES = 12

function asBytes(data: Uint8Array): Uint8Array {
	return new Uint8Array(data)
}

async function encryptSeedGcm(key: Uint8Array, phrase: string): Promise<string> {
	const iv = crypto.getRandomValues(new Uint8Array(SEED_GCM_IV_BYTES))
	const cryptoKey = await crypto.subtle.importKey('raw', asBytes(key), { name: 'AES-GCM' }, false, [
		'encrypt'
	])
	const ciphertext = await crypto.subtle.encrypt(
		{ name: 'AES-GCM', iv },
		cryptoKey,
		new TextEncoder().encode(phrase)
	)
	return `${SEED_GCM_PREFIX}${Buffer.concat([Buffer.from(iv), Buffer.from(ciphertext)]).toString(
		'hex'
	)}`
}

async function decryptSeedGcm(key: Uint8Array, stored: string): Promise<string> {
	const packed = Buffer.from(stored.slice(SEED_GCM_PREFIX.length), 'hex')
	if (packed.length <= SEED_GCM_IV_BYTES) {
		throw new Error('Invalid encrypted seed')
	}
	const iv = packed.subarray(0, SEED_GCM_IV_BYTES)
	const ciphertext = packed.subarray(SEED_GCM_IV_BYTES)
	const cryptoKey = await crypto.subtle.importKey('raw', asBytes(key), { name: 'AES-GCM' }, false, [
		'decrypt'
	])
	const plain = await crypto.subtle.decrypt(
		{ name: 'AES-GCM', iv: asBytes(iv) },
		cryptoKey,
		asBytes(ciphertext)
	)
	return new TextDecoder().decode(plain)
}

async function decryptSeedCtr(key: Uint8Array, stored: string): Promise<string> {
	const { AES, AESAlgorithms } = await import('@/utils/sdk.wasm')
	const decrypted = AES.decrypt(
		key,
		LEGACY_SEED_IV,
		Buffer.from(stored, 'hex'),
		AESAlgorithms.AES256_CTR
	)
	return Buffer.from(decrypted).toString('utf8')
}

const startPasswordLoop = () => {
	if (typeof window === 'undefined') {
		return
	}

	if (DID_INIT_PASSWORD_LOOP === true) {
		return
	}

	DID_INIT_PASSWORD_LOOP = true

	// Timer runs for the lifetime of the module; we never clear it.
	// eslint-disable-next-line unused-imports/no-unused-vars
	const interval = setInterval(() => {
		const last_decrypted_at = new Date(SafeLocalStorage.getItem(STORAGE_LAST_ACTIVE_AT))
		const now = new Date()

		if (Math.abs(now.getTime() - last_decrypted_at.getTime()) > DEFAULT_DURATION) {
			SafeLocalStorage.setItem(STORAGE_LAST_ACTIVE_AT, new Date().toISOString(), OVERWRITE)
			PASSWORD = ''
		}
	}, 1000 * 15) // 15 Seconds
}
startPasswordLoop()

export const setPassword = async (
	password: string,
	phrase: string,
	options: { exactBytes?: boolean } = {}
): Promise<void> => {
	if (!password) {
		return
	}

	const [{ Hash, Authentication, PrivateKey }, { publicKeyToAddress, seedToKeypair }] =
		await Promise.all([import('@/utils/sdk.wasm'), import('@/utils/bitcoin')])

	// Derive before writing so a bad phrase never lands in storage. Mnemonics are
	// stored in validateMnemonic form; private keys stay byte-for-byte.
	const { public_key, type } = await seedToKeypair(phrase, { exact: options.exactBytes })
	const phraseToStore =
		type === 'pk' || options.exactBytes ? phrase : await validateMnemonic(phrase)

	const salt = Hash.sha_256(Buffer.from(PrivateKey.from_random().to_bytes())).to_hex()
	const cipher = Authentication.getCipher(salt, password)
	const key = Buffer.from(cipher.getPasswordHash(), 'hex')
	const encryptedSeed = await encryptSeedGcm(key, phraseToStore)

	SafeLocalStorage.setItem(STORAGE_ENCRYPTED_SEED_SALT, salt)
	SafeLocalStorage.setItem(STORAGE_ENCRYPTED_SEED, encryptedSeed)
	SafeLocalStorage.setItem(STORAGE_LAST_ACTIVE_AT, new Date().toISOString(), OVERWRITE)

	const { address } = await publicKeyToAddress(public_key)

	SafeLocalStorage.setItem(STORAGE_ENCRYPTED_SEED_TYPE, type)
	SafeLocalStorage.setItem(STORAGE_TAPROOT_ADDRESS, address)
	SafeLocalStorage.setItem(STORAGE_PUBLIC_KEY, public_key)
	SafeLocalStorage.setItem(STORAGE_WALLET_TYPE, 'ordinalswallet')

	OrdinalsWallet.register()
	OrdinalsWallet.fetchProfile()

	PASSWORD = password
}

export const isPasswordUnlocked = () => {
	return !!PASSWORD
}

export const getSeed = async (password?: string): Promise<{ valid: boolean; seed: string }> => {
	if (!PASSWORD && !password) {
		Event.emit('open-password-modal')
		return { valid: false, seed: null as any }
	}

	const p = PASSWORD || password || ''

	const [{ Authentication }, { privateKeyToKeypair, seedToKeypair }] = await Promise.all([
		import('@/utils/sdk.wasm'),
		import('@/utils/bitcoin')
	])

	const salt = SafeLocalStorage.getItem(STORAGE_ENCRYPTED_SEED_SALT)

	const cipher = Authentication.getCipher(salt, p as any)
	const key = Buffer.from(cipher.getPasswordHash(), 'hex')

	const encryptedSeed = SafeLocalStorage.getItem(STORAGE_ENCRYPTED_SEED)

	let seed: string
	let legacyCtr = false
	try {
		if (typeof encryptedSeed === 'string' && encryptedSeed.startsWith(SEED_GCM_PREFIX)) {
			seed = await decryptSeedGcm(key, encryptedSeed)
		} else {
			seed = await decryptSeedCtr(key, encryptedSeed)
			legacyCtr = true
		}
	} catch {
		return { valid: false, seed: null as any }
	}

	let validSeed = false
	let seedOut = seed

	if (SafeLocalStorage.getItem(STORAGE_ENCRYPTED_SEED_TYPE) === 'pk') {
		try {
			await privateKeyToKeypair(seed)
			validSeed = true
		} catch (_e) {
			validSeed = false
		}
	} else {
		try {
			// Checksum, not word count. Also folds case/spacing so unlock matches restore.
			seedOut = await validateMnemonic(seed)
			validSeed = true
			// A pre-normalization wallet that lives at the raw-bytes derivation
			// keeps its raw phrase (see seedToKeypair); normalizing it would
			// move the user to a different key.
			if (seedOut !== seed && (await seedToKeypair(seed)).legacy_raw) {
				seedOut = seed
			}
		} catch (_e) {
			validSeed = false
		}
	}

	// Rewrite a successful legacy CTR unlock as GCM. A bad password never gets here
	// (CTR garbage fails the checksum), so this cannot clobber a real seed.
	if (validSeed && legacyCtr) {
		try {
			const upgraded = await encryptSeedGcm(key, seedOut)
			SafeLocalStorage.setItem(STORAGE_ENCRYPTED_SEED, upgraded, OVERWRITE)
		} catch (e) {
			console.error(e)
		}
	}

	SafeLocalStorage.setItem(STORAGE_LAST_ACTIVE_AT, new Date().toISOString(), OVERWRITE)
	if (validSeed) {
		PASSWORD = p
	}
	return { valid: validSeed, seed: validSeed ? seedOut : seed }
}

export const removePassword = () => (PASSWORD = '')

bindPasswordLock(() => !PASSWORD)
